/**
 * The trimmed enrichment surfaces (design C.1).
 *
 * The card is the old preview block, restored: the "AI Enrichment" header with
 * the status badge, the enricher's confidence, the entity's `about`, and every
 * feature bar the enricher wrote — 0% included, because "the enricher looked and
 * scored this low" is a different fact from "nobody looked". The link at the
 * bottom is the one route into `/<resource>/enrichment/:id`.
 *
 * `FeatureBars` stays exported here because the full editor draws the same bar
 * with grouping; this file's copy is the flat, sorted one.
 */
import React from "react";
import { Link } from "react-router";

import { FlaskConical } from "lucide-react";

import { EnrichmentStatusBadge } from "@/components/common/enrichment-status-badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";

import { enrichmentConfigFor } from "./config";
import { labelForKey } from "./field-rules";

export interface EnrichmentSummaryProps {
  resource: string;
  id: string;
  status?: string | null;
  enrichment: Record<string, unknown> | null | undefined;
  className?: string;
  /**
   * Draw a `Card` border. False by default: the preview asides set the card
   * inside their own panel, where a second frame reads as a box in a box.
   */
  bordered?: boolean;
  /** Label map for the feature keys. Unnamed keys fall back to `labelForKey`. */
  featureLabels?: Record<string, string>;
  /**
   * Draw the "View Enrichment" button. The preview asides draw their own, so
   * they turn this off to avoid two buttons into the same page.
   */
  showLink?: boolean;
}

/** True for a value that has at least one member — `{}` reads as "nothing". */
function hasMembers(enrichment: Record<string, unknown> | null | undefined) {
  return (
    !!enrichment &&
    typeof enrichment === "object" &&
    Object.keys(enrichment).length > 0
  );
}

/** `enrichment.reasoning.confidence`, a 0-1 fraction, when the enricher wrote one. */
function confidenceOf(enrichment: Record<string, unknown>): number | null {
  const reasoning = enrichment.reasoning;
  if (!reasoning || typeof reasoning !== "object") return null;
  const value = (reasoning as Record<string, unknown>).confidence;
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

/** The entity's `about`, or the config's one-liner when the enricher wrote none. */
function aboutOf(
  enrichment: Record<string, unknown>,
  summaryOf: (enrichment: Record<string, unknown>) => string | undefined,
): string | undefined {
  const about = enrichment.about;
  if (typeof about === "string" && about.trim()) return about;
  return summaryOf(enrichment);
}

export function EnrichmentSummary({
  resource,
  id,
  status,
  enrichment,
  className,
  bordered = false,
  featureLabels,
  showLink = true,
}: EnrichmentSummaryProps): React.JSX.Element | null {
  const present = hasMembers(enrichment);
  if (!present && !status) return null;

  const config = enrichmentConfigFor(resource);
  const record = (enrichment ?? {}) as Record<string, unknown>;
  const confidence = present ? confidenceOf(record) : null;
  const about = present ? aboutOf(record, config.summaryOf) : undefined;

  const body = (
    <div className={cn("flex flex-col gap-2", className)}>
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          AI Enrichment
        </span>
        <EnrichmentStatusBadge
          status={status ?? "Unavailable"}
          className="text-xs"
        />
      </div>

      {present && (
        <div className="flex flex-col gap-1.5">
          {confidence != null && (
            <span className="text-xs text-muted-foreground">
              {Math.round(confidence * 100)}% confidence
            </span>
          )}
          {about && (
            <p className="line-clamp-3 text-xs leading-relaxed text-muted-foreground">
              {about}
            </p>
          )}
          <FeatureBars
            // The read schema is `.passthrough()`, so its output carries an
            // `unknown` catchall; the scores are numbers at runtime.
            features={
              record.features as
                | Record<string, number | null | undefined>
                | undefined
            }
            labels={featureLabels}
            className="pt-0.5"
          />
        </div>
      )}

      {showLink && (
        <Button asChild size="sm" variant="outline" className="w-full gap-1.5">
          <Link to={`/${config.path}/enrichment/${id}`}>
            <FlaskConical className="h-3.5 w-3.5" />
            View Enrichment
          </Link>
        </Button>
      )}
    </div>
  );

  if (!bordered) return body;

  return (
    <Card className="w-full">
      <CardContent className="p-4">{body}</CardContent>
    </Card>
  );
}

export interface FeatureBarsProps {
  features: Record<string, number | null | undefined> | null | undefined;
  labels?: Record<string, string>;
  /** Keep only the `max` highest dimensions. All of them by default. */
  max?: number;
  className?: string;
}

/**
 * The feature dimensions of an entity, highest first.
 *
 * Every scored dimension is drawn, 0% included: a zero is the enricher saying
 * "not this", which is the answer a curator is looking for half the time.
 */
export function FeatureBars({
  features,
  labels,
  max,
  className,
}: FeatureBarsProps): React.JSX.Element | null {
  const sorted = Object.entries(features ?? {})
    .filter(([, value]) => typeof value === "number" && Number.isFinite(value))
    .sort(([, a], [, b]) => (b as number) - (a as number));
  const entries = typeof max === "number" ? sorted.slice(0, max) : sorted;

  if (entries.length === 0) return null;

  return (
    <div className={cn("flex flex-col gap-1", className)}>
      {entries.map(([key, value]) => {
        const score = value as number;
        return (
          <div
            key={key}
            data-testid="feature-bar"
            className="flex items-center gap-2"
          >
            <span className="w-20 shrink-0 truncate text-xs text-muted-foreground">
              {labels?.[key] ?? labelForKey(key)}
            </span>
            <div className="h-1 flex-1 overflow-hidden rounded-full bg-muted">
              <div
                className={cn(
                  "h-full rounded-full",
                  score >= 0.7
                    ? "bg-emerald-500"
                    : score >= 0.4
                      ? "bg-amber-500"
                      : "bg-muted-foreground/40",
                )}
                style={{ width: `${Math.round(score * 100)}%` }}
              />
            </div>
            <span className="w-7 text-right font-mono text-xs text-muted-foreground">
              {Math.round(score * 100)}%
            </span>
          </div>
        );
      })}
    </div>
  );
}

export default EnrichmentSummary;
